/**
 * Presentational contract for the directory card. No Prisma types, no tRPC
 * output types: the page/router layer maps its query result onto this shape,
 * which keeps the card usable in Storybook without a database.
 */

export type PluralForms = { one: string; few: string; many: string };

/**
 * Serbian plural agreement: 1/21/31... "one", 2-4/22-24... "few", otherwise
 * "many". Shared here so every consumer (review counts, session counts,
 * skeleton copy) agrees on one rule instead of reimplementing the
 * mod-10/mod-100 logic the way the lab prototype did inline.
 */
export function countWithNoun(count: number, forms: PluralForms): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} ${forms.one}`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14))
    return `${count} ${forms.few}`;
  return `${count} ${forms.many}`;
}

export const REVIEW_FORMS: PluralForms = {
  one: "ocena",
  few: "ocene",
  many: "ocena",
};

const SESSION_FORMS: PluralForms = {
  one: "sesija",
  few: "sesije",
  many: "sesija",
};

/** 1240 -> "1.200+ sesija"; small counts stay exact and honest. */
export function sessionsLabel(count: number): string {
  if (count >= 100) {
    const rounded = Math.floor(count / 100) * 100;
    return `${rounded.toLocaleString("sr-Latn-RS")}+ sesija`;
  }
  return countWithNoun(count, SESSION_FORMS);
}

export type DiscoverySurface =
  | "directory"
  | "specialty_directory"
  | "quiz_results";

export interface MatchInfo {
  reasons: string[];
  isBest?: boolean;
}

export interface TherapistCardData {
  id: string;
  slug: string;
  name: string;
  image: string | null;
  /** Verbatim `Therapist.qualificationCategory`, the declared qualification category. */
  tier: string | null;
  /** Verbatim `Therapist.title`, self-written by the therapist and unverified. */
  title: string | null;
  isVerified: boolean;
  isFeatured: boolean;
  offersIntroCall: boolean;
  reviewCount: number;
  /** Completed-session / distinct-client proof number (the italki/Preply number). */
  sessionsHeld: number;
  languages: string[];
  /** Individual session price and length (`defaultPrice`, `individualSessionDuration`). */
  price: number;
  sessionDurationMinutes: number;
  couplesTherapyPrice: number | null;
  couplesSessionDuration: number | null;
  videoIntroductionUrl: string | null;
  /** Ordered canonical concern keys, primary first. */
  focusAreasTop: string[];
  focusAreaLabels?: Record<string, string>;
  /** First declared approach, or null if unstated. */
  modalityPrimary: string | null;
  modalityLabel?: string;
}

export type DailyAvailability =
  | { state: "loading" }
  | { state: "unavailable" }
  | { state: "ready"; days: Array<{ date: string; slotCount: number }> }
  | { state: "break"; until: string };

export interface TherapistCardProps {
  therapist: TherapistCardData;
  /** Set by the page, exactly as today: true only on `/terapija/za-parove`. */
  isCouplesTherapy: boolean;
  availability: DailyAvailability;
  match?: MatchInfo;
  /** Caveats surfaced only on the `/upitnik/[resultId]` quiz-results surface. */
  missingAttributes?: string[];
  discoverySurface?: DiscoverySurface;
  specialtySlug?: string;
  /** Keeps the reversible couples-directory mode when navigating from a result. */
  preserveCouplesQuery?: boolean;
  resultRank?: number;
  renderJsonLd?: boolean;
  /** Optional custom content to render instead of the availability column. */
  primaryAction?: React.ReactNode;
}

export interface ResolvedService {
  type: "INDIVIDUAL" | "COUPLES";
  label: string;
  minutes: number;
  price: number;
}

/**
 * One service per card, never both: the same resolution as
 * `therapist-booking-buttons.tsx:132`, so the day-tile booking URL, the price
 * shown and what actually gets booked can never disagree.
 */
export function resolveService(
  therapist: Pick<
    TherapistCardData,
    | "price"
    | "sessionDurationMinutes"
    | "couplesTherapyPrice"
    | "couplesSessionDuration"
  >,
  isCouplesTherapy: boolean,
): ResolvedService {
  if (isCouplesTherapy && therapist.couplesTherapyPrice) {
    return {
      type: "COUPLES",
      label: "Za parove",
      minutes: therapist.couplesSessionDuration ?? 60,
      price: therapist.couplesTherapyPrice,
    };
  }

  return {
    type: "INDIVIDUAL",
    label: "Individualno",
    minutes: therapist.sessionDurationMinutes,
    price: therapist.price,
  };
}
